import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { fabricService } from '../../services';

const FabricMaster = () => {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const response = await fabricService.getAll({ search: searchTerm });
      setItems(response.data || []);
    } catch (e) {
      console.error('Error loading fabrics:', e);
      toast.error('Failed to load fabrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
    if (item) {
      reset({ fabric: item.fabric, description: item.description, status: item.status });
    } else {
      reset({ fabric: '', description: '', status: 1 });
    }
  };

  const closeModal = () => { setShowModal(false); setEditingItem(null); reset(); };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingItem) {
        await fabricService.update(editingItem.id, data);
        toast.success('Fabric updated successfully!');
      } else {
        await fabricService.create(data);
        toast.success('Fabric created successfully!');
      }
      closeModal();
      load();
    } catch (e) {
      console.error('Error saving fabric:', e);
      toast.error('Failed to save fabric');
    } finally { setLoading(false); }
  };

  const toggleStatus = async (item) => {
    try {
      setLoading(true);
      await fabricService.toggleStatus(item.id);
      toast.success('Fabric status updated!');
      load();
    } catch (e) {
      console.error('Error toggling status:', e);
      toast.error('Failed to update status');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>Fabric Master</h1>
          <button onClick={() => openModal()} className="btn btn-primary">
            <Plus size={18} />
            Add Fabric
          </button>
        </div>

        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input type="text" placeholder="Search fabrics..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-box" style={{ paddingLeft: '40px' }} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Fabric</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td style={{ fontWeight: '500' }}>{item.fabric}</td>
                  <td>
                    <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {item.description || '-'}
                    </div>
                  </td>
                  <td>
                    <button onClick={() => toggleStatus(item)} className={`status-badge ${item.status === 1 ? 'status-active' : 'status-inactive'}`} style={{ cursor: 'pointer', border: 'none' }} disabled={loading}>
                      {item.status === 1 ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td>{new Date(item.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => openModal(item)} className="btn btn-secondary" style={{ padding: '6px 8px', fontSize: '12px' }}>
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading fabrics...</div>)}
        {!loading && items.length === 0 && (<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No fabrics found.</div>)}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingItem ? 'Edit Fabric' : 'Add New Fabric'}</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Fabric</label>
                <input type="text" className="form-input" placeholder="Enter fabric name" {...register('fabric', { required: 'Fabric is required', minLength: { value: 2, message: 'Fabric must be at least 2 characters' } })} />
                {errors.fabric && (<div className="error-message">{errors.fabric.message}</div>)}
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" placeholder="Enter description (optional)" rows={4} {...register('description')} />
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" {...register('status', { required: 'Status is required' })}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
                {errors.status && (<div className="error-message">{errors.status.message}</div>)}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" onClick={closeModal} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : editingItem ? 'Update Fabric' : 'Create Fabric'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FabricMaster;
